// ============================================
// GroupInfoModal Component
// Modal for viewing and managing group information
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase/client';
import { addGroupMember, removeGroupMember, uploadGroupChatAvatar } from '@/lib/supabase/chat';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2, Users, Crown, UserMinus, Edit3, Save, X, UserPlus, Copy, Link as LinkIcon, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GroupMember {
  id: string;
  user_id: string;
  username: string;
  display_name?: string;
  avatar_url?: string;
  joined_at: string;
  role: 'admin' | 'member';
}

interface GroupInfo {
  id: string;
  name: string;
  description?: string;
  avatar_url?: string | null;
  created_at: string;
  created_by: string;
  member_count: number;
}

interface UserSearchResult {
  id: string;
  username: string;
  display_name?: string;
  avatar_url?: string;
}

interface GroupInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  initialAvatarUrl?: string | null;
  onGroupUpdated?: (payload: { name?: string; avatarUrl?: string | null }) => void;
}

export const GroupInfoModal: React.FC<GroupInfoModalProps> = ({
  isOpen,
  onClose,
  groupId,
  initialAvatarUrl,
  onGroupUpdated,
}) => {
  const [groupInfo, setGroupInfo] = useState<GroupInfo | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [addMemberQuery, setAddMemberQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [updatingMembers, setUpdatingMembers] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { toast } = useToast();

  // Load group information and members
  useEffect(() => {
    if (isOpen && groupId) {
      loadGroupInfo();
      loadGroupMembers();
    }
  }, [isOpen, groupId]);

  useEffect(() => {
    if (!isOpen) {
      setAddMemberQuery('');
      setUserSearchResults([]);
      setInviteLink('');
      return;
    }

    const trimmed = addMemberQuery.trim();
    if (trimmed.length < 2) {
      setUserSearchResults([]);
      return;
    }

    const timeout = setTimeout(() => {
      void searchUsers(trimmed);
    }, 250);

    return () => clearTimeout(timeout);
  }, [addMemberQuery, isOpen]);

  const loadGroupInfo = async () => {
    try {
      setLoading(true);

      const { data: groupRows, error } = await supabase
        .from('group_chats')
        .select('id, name, description, avatar_url, created_at, created_by')
        .eq('id', groupId)
        .limit(1);

      if (error) {
        // En algunos entornos, la lectura directa de group_chats puede fallar por RLS.
        // Continuamos con fallback RPC para evitar romper el modal.
        console.debug('GroupInfoModal: direct group_chats read failed, using RPC fallback', error);
      }

      let resolvedGroup: any = groupRows?.[0] || null;

      // Fallback: cuando RLS o caches no devuelven fila desde la tabla directa.
      if (!resolvedGroup) {
        const { data: rpcGroups, error: rpcError } = await supabase.rpc('get_user_group_chats');
        if (!rpcError && Array.isArray(rpcGroups)) {
          const groupFromRpc = rpcGroups.find((entry: any) => entry?.id === groupId);
          if (groupFromRpc) {
            resolvedGroup = {
              id: groupFromRpc.id,
              name: groupFromRpc.name,
              description: groupFromRpc.description,
              avatar_url: groupFromRpc.avatar_url,
              created_at: new Date().toISOString(),
              created_by: '',
            };
          }
        }
      }

      if (!resolvedGroup) throw new Error('No se encontro informacion del grupo.');

      // Get member count
      const { count: memberCount } = await supabase
        .from('group_chat_members')
        .select('*', { count: 'exact', head: true })
        .eq('group_chat_id', groupId);

      setGroupInfo({
        ...resolvedGroup,
        member_count: memberCount || 0,
      });
      setEditName(resolvedGroup.name);
      setEditDescription(resolvedGroup.description || '');
    } catch (error: any) {
      console.error('Error loading group info:', error);
      toast({
        title: 'Error al cargar información del grupo',
        description: error.message || 'Ocurrió un error.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadGroupMembers = async () => {
    try {
      const { data: membersData, error } = await supabase
        .from('group_chat_members')
        .select('id, user_id, joined_at, role')
        .eq('group_chat_id', groupId)
        .order('joined_at', { ascending: true });

      if (error) throw error;

      let effectiveMembersData = membersData || [];

      // Fallback: algunos proyectos devuelven 0 filas por RLS/caches en group_chat_members.
      // En ese caso, hidratamos miembros desde remitentes del historial del grupo.
      if (effectiveMembersData.length === 0) {
        const { data: groupMessages, error: messagesError } = await supabase.rpc('get_group_messages', {
          group_id: groupId,
          limit_count: 1000,
          offset_count: 0,
        });

        if (!messagesError && Array.isArray(groupMessages) && groupMessages.length > 0) {
          const senderIds = Array.from(
            new Set(
              groupMessages
                .map((message: any) => message?.sender_id)
                .filter(Boolean)
            )
          );

          effectiveMembersData = senderIds.map((senderId: string, index: number) => ({
            id: `fallback-${senderId}`,
            user_id: senderId,
            joined_at: new Date(Date.now() + index).toISOString(),
            role: 'member',
          }));
        }
      }

      const userIds = effectiveMembersData
        .map((member: any) => member.user_id)
        .filter(Boolean);

      let profilesById = new Map<string, any>();
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, display_name, avatar_url')
          .in('id', userIds);

        if (!profilesError) {
          profilesById = new Map((profilesData || []).map((profile: any) => [profile.id, profile]));
        }
      }

      const missingProfileIds = userIds.filter((userId: string) => !profilesById.has(userId));
      if (missingProfileIds.length > 0) {
        const { data: groupMessages, error: messagesError } = await supabase.rpc('get_group_messages', {
          group_id: groupId,
          limit_count: 500,
          offset_count: 0,
        });

        if (!messagesError && Array.isArray(groupMessages)) {
          groupMessages.forEach((message: any) => {
            if (!message?.sender_id || profilesById.has(message.sender_id)) return;
            profilesById.set(message.sender_id, {
              id: message.sender_id,
              username: message.sender_username || 'usuario',
              display_name: message.sender_display_name || null,
              avatar_url: message.sender_avatar_url || null,
            });
          });
        }
      }

      const formattedMembers: GroupMember[] = effectiveMembersData.map((member: any) => ({
        id: member.id,
        user_id: member.user_id,
        username: profilesById.get(member.user_id)?.username || 'usuario',
        display_name: profilesById.get(member.user_id)?.display_name,
        avatar_url: profilesById.get(member.user_id)?.avatar_url,
        joined_at: member.joined_at,
        role: member.role,
      }));

      setMembers(formattedMembers);
      setGroupInfo((prev) => prev ? {
        ...prev,
        member_count: Math.max(prev.member_count || 0, formattedMembers.length),
      } : prev);
    } catch (error: any) {
      console.error('Error loading group members:', error);
      toast({
        title: 'Error al cargar miembros',
        description: error.message || 'Ocurrió un error.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveChanges = async () => {
    if (!groupInfo) return;

    try {
      setLoading(true);

      const { data: updatedRows, error } = await supabase
        .from('group_chats')
        .update({
          name: editName,
          description: editDescription,
        })
        .eq('id', groupId)
        .select('id, name, description');

      if (error) throw error;

      const updatedGroup = Array.isArray(updatedRows) ? updatedRows[0] : null;
      if (!updatedGroup) {
        throw new Error('No se pudo actualizar el grupo en base de datos (sin permisos o grupo inexistente).');
      }

      setGroupInfo(prev => prev ? {
        ...prev,
        name: updatedGroup.name,
        description: updatedGroup.description,
      } : null);
      onGroupUpdated?.({ name: updatedGroup.name });

      setEditing(false);

      toast({
        title: 'Grupo actualizado',
        description: 'Los cambios han sido guardados exitosamente.',
      });
    } catch (error: any) {
      console.error('Error updating group:', error);
      toast({
        title: 'Error al actualizar grupo',
        description: error.message || 'Ocurrió un error.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      setUpdatingMembers(true);

      await removeGroupMember(groupId, memberId);

      setMembers(prev => prev.filter(member => member.user_id !== memberId));

      toast({
        title: 'Miembro removido',
        description: 'El miembro ha sido removido del grupo.',
      });
    } catch (error: any) {
      console.error('Error removing member:', error);
      toast({
        title: 'Error al remover miembro',
        description: error.message || 'Ocurrió un error.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingMembers(false);
    }
  };

  const canEditGroup = true;

  const handleChangeGroupAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !groupInfo || !canEditGroup) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Archivo no valido',
        description: 'Selecciona una imagen para el grupo.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      toast({
        title: 'Imagen muy pesada',
        description: 'La imagen no puede superar 8 MB.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploadingAvatar(true);
      const nextAvatarUrl = await uploadGroupChatAvatar(file);
      const { data: updatedRows, error } = await supabase
        .from('group_chats')
        .update({ avatar_url: nextAvatarUrl })
        .eq('id', groupId)
        .select('id, avatar_url');

      if (error) throw error;

      const updatedGroup = Array.isArray(updatedRows) ? updatedRows[0] : null;
      if (!updatedGroup) {
        throw new Error('No se pudo guardar avatar en base de datos (sin permisos o grupo inexistente).');
      }

      if (updatedGroup.avatar_url !== nextAvatarUrl) {
        throw new Error('El avatar no quedo persistido en avatar_url.');
      }

      setGroupInfo((prev) => (prev ? { ...prev, avatar_url: updatedGroup.avatar_url } : prev));
      onGroupUpdated?.({ avatarUrl: updatedGroup.avatar_url });

      toast({
        title: 'Foto actualizada',
        description: 'La imagen del grupo se actualizo correctamente.',
      });
    } catch (error: any) {
      console.error('Error updating group avatar:', error);
      toast({
        title: 'Error al actualizar foto',
        description: error.message || 'No se pudo actualizar la imagen del grupo.',
        variant: 'destructive',
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const searchUsers = async (query: string) => {
    try {
      setSearchingUsers(true);
      const safeQuery = query.replace(/[%_(),]/g, ' ').replace(/\s+/g, ' ').trim();
      if (!safeQuery) {
        setUserSearchResults([]);
        return;
      }

      const usernameReq = supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .ilike('username', `%${safeQuery}%`)
        .limit(12);

      const displayNameReq = supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url')
        .ilike('display_name', `%${safeQuery}%`)
        .limit(12);

      const [{ data: byUsername, error: byUsernameError }, { data: byDisplayName, error: byDisplayNameError }] = await Promise.all([
        usernameReq,
        displayNameReq,
      ]);

      if (byUsernameError && byDisplayNameError) {
        throw byUsernameError;
      }

      const mergedById = new Map<string, any>();
      [...(byUsername || []), ...(byDisplayName || [])].forEach((user: any) => {
        if (user?.id) mergedById.set(user.id, user);
      });
      const data = Array.from(mergedById.values());

      const currentMemberIds = new Set(members.map((member) => member.user_id));
      const filtered = (data || []).filter((user: any) => !currentMemberIds.has(user.id));
      setUserSearchResults(filtered);
    } catch (error: any) {
      console.error('Error searching users:', error);
      setUserSearchResults([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleAddMember = async (user: UserSearchResult) => {
    try {
      setUpdatingMembers(true);
      await addGroupMember(groupId, user.id);
      await loadGroupMembers();
      setUserSearchResults((prev) => prev.filter((candidate) => candidate.id !== user.id));

      toast({
        title: 'Miembro agregado',
        description: `@${user.username} fue agregado al grupo.`,
      });
    } catch (error: any) {
      console.error('Error adding member:', error);
      toast({
        title: 'Error al agregar miembro',
        description: error.message || 'No se pudo agregar el miembro.',
        variant: 'destructive',
      });
    } finally {
      setUpdatingMembers(false);
    }
  };

  const handleGenerateInviteLink = async () => {
    try {
      setCreatingInvite(true);
      const { data, error } = await supabase.rpc('create_or_get_group_invite_token', {
        group_id: groupId,
      });

      if (error) throw error;

      const token = String(data || '').trim();
      if (!token) throw new Error('No se pudo generar el token de invitación.');

      const link = `${window.location.origin}/chat?invite=${encodeURIComponent(token)}`;
      setInviteLink(link);
    } catch (error: any) {
      console.error('Error generating invite link:', error);
      toast({
        title: 'Error al generar enlace',
        description: error.message || 'No se pudo crear el enlace de invitación.',
        variant: 'destructive',
      });
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast({
        title: 'Enlace copiado',
        description: 'Comparte este enlace para que se unan al grupo.',
      });
    } catch {
      toast({
        title: 'No se pudo copiar',
        description: 'Copia el enlace manualmente.',
        variant: 'destructive',
      });
    }
  };

  if (!groupId) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            No se encontro el identificador del grupo.
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!groupInfo) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="ml-2">Cargando información del grupo...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const resolvedAvatarUrl = groupInfo.avatar_url || initialAvatarUrl || undefined;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Información del grupo
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0 space-y-4">
          {/* Group Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar className="w-14 h-14 rounded-lg">
                <AvatarImage src={resolvedAvatarUrl} />
                <AvatarFallback className="rounded-lg">
                  {(groupInfo.name || '?').slice(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleChangeGroupAvatar}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!canEditGroup || uploadingAvatar}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingAvatar ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 mr-1.5" />
                  )}
                  Editar foto
                </Button>
              </div>
            </div>

            {editing ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Nombre del grupo</Label>
                  <Input
                    id="edit-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Nombre del grupo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Descripción</Label>
                  <Textarea
                    id="edit-description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Descripción del grupo (opcional)"
                    className="min-h-[60px] resize-none"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <h3 className="font-semibold text-lg">{groupInfo.name}</h3>
                  {groupInfo.description && (
                    <p className="text-sm text-muted-foreground mt-1">{groupInfo.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{members.length} miembros</span>
                  <span>Creado {new Date(groupInfo.created_at).toLocaleDateString()}</span>
                </div>
              </>
            )}
          </div>

          {canEditGroup && (
            <div className="space-y-3 rounded-lg border border-primary/15 bg-primary/5 p-3">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm font-medium">Enlace de invitación</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleGenerateInviteLink}
                  disabled={creatingInvite}
                >
                  {creatingInvite ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-1.5" />}
                  {inviteLink ? 'Regenerar' : 'Generar'}
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  value={inviteLink}
                  readOnly
                  placeholder="Genera un enlace para invitar al equipo"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCopyInviteLink}
                  disabled={!inviteLink}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Quien abra el enlace podrá unirse al grupo desde la pantalla de chat.
              </p>
            </div>
          )}

          {canEditGroup && (
            <div className="space-y-3 rounded-lg border p-3">
              <Label className="text-sm font-medium">Agregar miembros</Label>
              <Input
                value={addMemberQuery}
                onChange={(event) => setAddMemberQuery(event.target.value)}
                placeholder="Buscar por @usuario o nombre"
              />
              <div className="max-h-44 overflow-auto space-y-2">
                {searchingUsers && (
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Buscando usuarios...
                  </div>
                )}
                {!searchingUsers && addMemberQuery.trim().length >= 2 && userSearchResults.length === 0 && (
                  <div className="text-xs text-muted-foreground">No hay usuarios disponibles para agregar.</div>
                )}
                {userSearchResults.map((user) => (
                  <div key={user.id} className="flex items-center justify-between rounded-md border p-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar className="w-7 h-7">
                        <AvatarImage src={user.avatar_url} />
                        <AvatarFallback>
                          {(user.display_name || user.username || '?').slice(0, 1).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{user.display_name || user.username}</div>
                        <div className="text-xs text-muted-foreground truncate">@{user.username}</div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddMember(user)}
                      disabled={updatingMembers}
                    >
                      <UserPlus className="w-3.5 h-3.5 mr-1" />
                      Agregar
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Members List */}
          <div className="flex-1 min-h-0">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">Miembros ({members.length})</Label>
            </div>

            <ScrollArea className="flex-1 border rounded-md p-2 max-h-[250px]">
              <div className="space-y-2">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={member.avatar_url} />
                        <AvatarFallback>
                          {(member.display_name || member.username).charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm truncate">
                            {member.display_name || member.username}
                          </p>
                          {member.role === 'admin' && (
                            <Crown className="w-3 h-3 text-yellow-500" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          @{member.username}
                        </p>
                      </div>
                    </div>

                    {canEditGroup && member.role !== 'admin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.user_id)}
                        disabled={loading || updatingMembers}
                        className="text-destructive button-destructive-ghost transition-colors"
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          {editing ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setEditName(groupInfo.name);
                  setEditDescription(groupInfo.description || '');
                }}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleSaveChanges}
                disabled={loading || !editName.trim()}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar cambios
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={onClose}>
                Cerrar
              </Button>
              {canEditGroup && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditing(true)}
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Editar grupo
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};